"use client";

import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { throttle } from "lodash-es";
import { useOspLoginProvider } from "@features/auth/compat/useOspLoginProvider";
import { getBridge, useBridge } from "@/src/lib/bridge";
import { ContainerBroadcastEvent, MessageType } from "@/src/lib/bridge/types";
import { getContainerRouteContext } from "@/src/lib/bridge/containerRouteMeta";
import { syncAuthSessionFromSharedStorage } from "@features/login/syncAuthSessionFromSharedStorage";
import { invalidateMemberQueries } from "@utils/memberInvalidateQueries";

/**
 * 监听 `auth-session-updated` 广播，从共享 localStorage 恢复 LoginStore / OSP 登录态。
 */
export function useCrossWebViewAuthSessionSync() {
  const { on, isInApp } = useBridge();
  const { authModule } = useOspLoginProvider();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!on) {
      return;
    }

    const flush = async (data: {
      event?: string;
      sourceTabIndex?: number;
      loginType?: number;
    }) => {
      const self = getContainerRouteContext();
      const src = data.sourceTabIndex;
      if (
        typeof src === "number" &&
        src >= 0 &&
        self.tabIndex != null &&
        src === self.tabIndex
      ) {
        return;
      }

      const restored = await syncAuthSessionFromSharedStorage(authModule);
      if (restored) {
        invalidateMemberQueries(queryClient, [], {
          skipCrossWebViewBroadcast: true,
        });
      }
    };

    const throttled = throttle(
      (data: Parameters<typeof flush>[0]) => {
        void flush(data);
      },
      400,
      { leading: true, trailing: true },
    );

    let unsubscribe: (() => void) | undefined;
    let pollTimer: ReturnType<typeof setInterval> | undefined;

    const attach = () => {
      if (!getBridge().isInApp()) {
        return false;
      }
      unsubscribe = on(
        MessageType.CONTAINER_BROADCAST,
        (data: {
          event?: string;
          sourceTabIndex?: number;
          loginType?: number;
          payload?: { loginType?: number; userId?: string };
        }) => {
          if (data?.event !== ContainerBroadcastEvent.AUTH_SESSION_UPDATED) {
            return;
          }
          throttled({
            event: data.event,
            sourceTabIndex: data.sourceTabIndex,
            loginType: data.loginType ?? data.payload?.loginType,
          });
        },
      );
      return true;
    };

    if (!attach()) {
      let retries = 0;
      const maxRetries = 300;
      pollTimer = setInterval(() => {
        retries += 1;
        if (attach() || retries >= maxRetries) {
          clearInterval(pollTimer);
          pollTimer = undefined;
        }
      }, 100);
    }

    return () => {
      if (pollTimer) {
        clearInterval(pollTimer);
      }
      unsubscribe?.();
      throttled.cancel();
    };
  }, [authModule, isInApp, on, queryClient]);
}
